import type { SettingsTemplateModelType } from '@/entities/settings-template'
import type {
	SettingsTemplateModel,
	SettingsTemplateType,
} from '@/shared/generated'

import { useMemo } from 'react'
import { groupBy } from 'lodash/fp'
import { SearchIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useSettingsTemplatesQuery } from '@/entities/settings-template'

import { SettingsTemplateGenaratedShortDescription } from '@/widgets/settings-template-description'

import { Operators } from '@/shared/components/query-builder'
import {
	CommandDialog,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/shared/components/ui/command'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { Skeleton } from '@/shared/components/ui/skeleton'

type TemplatesModal = {
	isOpen: boolean
	close(): void
	handleApply(props: SettingsTemplateModel): void
	template_types: SettingsTemplateType[]
}

export function SettingTemplatesModal({
	isOpen,
	close,
	handleApply,
	template_types,
}: TemplatesModal) {
	const { t } = useTranslation()

	const settingsTemplatesQuery = useSettingsTemplatesQuery({
		limit: 100,
		offset: 0,
		filters: [
			{
				name: 'template_type',
				operation: Operators.IN,
				value: template_types,
			},
		],
	})
	const isLoading = settingsTemplatesQuery.isLoading
	const handleSelect = (template: SettingsTemplateModel) => {
		handleApply(template)
		close()
	}
	const groupedOptions = useMemo(
		() =>
			Object.entries(
				groupBy('template_type', settingsTemplatesQuery.data?.data)
			).map(([groupKey, items]) => ({
				heading: t(`template.type.${groupKey}`, {
					defaultValue: groupKey || '',
				}),
				options: items,
			})),
		[settingsTemplatesQuery.data?.data]
	)

	return (
		<CommandDialog
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<CommandInput
				placeholder={t('general_search', { defaultValue: 'Search' })}
			/>
			<CommandList>
				{isLoading && groupedOptions.length === 0 && <DefaultLoadingSkeleton />}
				{!isLoading && (
					<CommandEmpty>
						<Empty>
							<EmptyHeader>
								<EmptyMedia variant='icon'>
									<SearchIcon className='text-muted-foreground' />
								</EmptyMedia>
								<EmptyTitle>
									{t('general_no_templates', {
										defaultValue: 'There is no templates',
									})}
								</EmptyTitle>
								<EmptyDescription>
									{t('general_no_templates_description', {
										defaultValue:
											'There are no templates available for the selected type or search.',
									})}
								</EmptyDescription>
							</EmptyHeader>
						</Empty>
					</CommandEmpty>
				)}
				{groupedOptions.map((group) => (
					<CommandGroup key={group.heading} heading={group.heading}>
						{group.options.map((template) => (
							<CommandItem
								key={template.id}
								value={template.name}
								onSelect={() => handleSelect(template)}
							>
								<div className='w-full cursor-pointer'>
									<div className='text-sm font-medium'>{template.name}</div>
									<SettingsTemplateGenaratedShortDescription
										template={
											{
												template_type: template.template_type,
												body: template.body,
											} as SettingsTemplateModelType
										}
									/>
								</div>
							</CommandItem>
						))}
					</CommandGroup>
				))}
			</CommandList>
		</CommandDialog>
	)
}
function DefaultLoadingSkeleton() {
	return (
		<CommandGroup>
			{[1, 2, 3].map((i) => (
				<CommandItem key={i} disabled className='bg-secondary'>
					<div className='flex flex-1 flex-col gap-1'>
						<Skeleton className='h-4 w-24 animate-pulse rounded bg-tertiary' />
						<Skeleton className='h-3 w-16 animate-pulse rounded bg-tertiary' />
					</div>
				</CommandItem>
			))}
		</CommandGroup>
	)
}
